// Issue #700: surface the calibration-needs predicate's blocked_reason as a
// chip on the calibration-needs page. Keeps the label/color mapping in one
// place so the admin and user views stay in sync.
//
// Shape mirrors the ReadinessReason enum in
// packages/server/src/services/calibrationReadiness.ts.

/**
 * @param {object} group  Accumulation entry from /v1/calibration/needs.
 * @returns {{label: string, color: 'success'|'warning'|'default', variant?: 'outlined', tooltip?: string} | null}
 *   Returns null when the group is ready_for_master (caller renders the
 *   existing "Ready" chip, or the "Needs Dark" branch on flats where the
 *   prerequisite_dark_available signal disagrees with the predicate).
 */
export function calibrationStatusChip(group) {
    if (group.ready_for_master) return null;

    const reason = group.blocked_reason;
    const total = group.usable_count != null ? group.usable_count : group.count;

    // Reason-specific chips come FIRST, before the count-based branch below.
    // temperature_scatter (and any future below-threshold-but-specific reason)
    // always has usable_count < target, so if the count branch ran first it would
    // swallow them into a misleading "Need N more". Order is the fix (#dead-branch).
    if (reason === 'no_dark_master_for_exposure') {
        const readout = group.readout_mode || 'default readout';
        const binning = group.binning || '1x1';
        const exptime = group.exposure_time_seconds != null
            ? `${group.exposure_time_seconds}s` : 'matching-exposure';
        return {
            label: `Blocked — needs ${readout} ${binning} ${exptime} master dark`,
            color: 'warning',
            tooltip: 'Capture or upload a matching master dark to unblock this flat.',
        };
    }

    if (reason === 'no_dark_master') {
        const readout = group.readout_mode || 'default readout';
        const binning = group.binning || '1x1';
        return {
            label: `Blocked — needs ${readout} ${binning} master dark`,
            color: 'warning',
            tooltip: 'No master dark exists for this camera config; build one first.',
        };
    }

    if (reason === 'temperature_scatter') {
        const captured = group.count;
        const usable = group.usable_count != null ? group.usable_count : group.count;
        return {
            label: `Blocked — temperature inconsistent (${usable}/${captured} usable)`,
            color: 'warning',
            tooltip: 'Captured frames span too wide a temperature range. Recapture under stable cooler conditions.',
        };
    }

    // Below-threshold accumulation (or any unknown reason): show the frame delta.
    // The blocked_reason 'insufficient_frames' is informative but the count delta is
    // what the operator acts on. This is the catch-all, so it stays LAST.
    if (reason === 'insufficient_frames' || total < group.target) {
        return {
            label: `Need ${group.target - total} more`,
            color: 'default',
            variant: 'outlined',
        };
    }

    // Unknown reason with total >= target — fail safe so the cell is never empty.
    return {
        label: `Need ${Math.max(group.target - total, 0)} more`,
        color: 'default',
        variant: 'outlined',
    };
}
